import './styles.css';
// Pioneer Design System
// Purpose:
// - Single import surface for app code
// - Centralize MUI usage inside design-system
// - Provide stable wrappers that we can evolve without touching app/screens

import * as _Icons from './icons';

export { PanelPaper } from "./PanelPaper";
export { CenteredText } from "./CenteredText";
export { PanelDivider } from "./PanelDivider";
export { BrandLogo } from "./BrandLogo";
export { HashtagChip } from "./HashtagChip";
export { default as Box } from './Box';
export { default as Button } from './Button';
export { default as Divider } from './Divider';
export { default as List } from './List';
export { ListItem, ListItemText, ListItemButton } from './List';
export { default as Paper } from './Paper';
export { default as Input } from './Input';
export { default as Typography } from './Typography';
export { default as Dialog } from './Dialog';
export { default as DialogTitle } from './DialogTitle';
export { default as DialogContent } from './DialogContent';
export { default as DialogActions } from './DialogActions';
export { default as DialogContentText } from './DialogContentText';
export { default as Table } from './Table';
export { TableBody, TableCell, TableHead, TableRow } from './Table';
export { default as TableContainer } from './TableContainer';
export { default as CircularProgress } from './CircularProgress';
export { default as CardContent } from './CardContent';
export { default as CardActions } from './CardActions';
export { default as IconButton } from './IconButton';
export { default as Tooltip } from './Tooltip';
export { default as Stack } from './Stack';
export { default as Skeleton } from './Skeleton';
export { useTheme } from '@mui/material/styles';
export { default as useMediaQuery } from '@mui/material/useMediaQuery';

// theme utilities (temporary re-exports to keep app code off direct MUI imports)
export { createTheme, alpha, styled } from '@mui/material/styles';
export { lightPalette, darkPalette } from './themes/muiPalette';

// MUI classes (used by theme override files)
export { svgIconClasses } from '@mui/material/SvgIcon';
export { typographyClasses } from '@mui/material/Typography';
export { buttonBaseClasses } from '@mui/material/ButtonBase';
export { chipClasses } from '@mui/material/Chip';
export { iconButtonClasses } from '@mui/material/IconButton';
export { outlinedInputClasses } from '@mui/material/OutlinedInput';
export { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
export { toggleButtonClasses } from '@mui/material/ToggleButton';
export { dividerClasses } from '@mui/material/Divider';
export { menuItemClasses } from '@mui/material/MenuItem';
export { selectClasses } from '@mui/material/Select';
export { tabClasses } from '@mui/material/Tab';

// MUI X date pickers (temporary re-exports until we wrap them)
export { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
export { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
export { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// form primitives
export { default as FormControl } from './FormControl';
export { default as InputLabel } from './InputLabel';
export { default as InputAdornment } from '@mui/material/InputAdornment';
export { default as FormLabel } from './FormLabel';
export { default as FormControlLabel } from './FormControlLabel';
export { default as Select } from './Select';
export { default as MenuItem } from './MenuItem';
export { default as Menu } from './Menu';
export { default as Link } from './Link';
export { default as TextField } from './TextField';
export { default as CssBaseline } from './CssBaseline';

// temporary re-exports to unblock app code during migration
export { default as AppBar } from './AppBar';
export { default as Toolbar } from './Toolbar';
export { default as Chip } from './Chip';
export { default as Snackbar } from './Snackbar';
export { default as Alert } from './Alert';
export { default as OutlinedInput } from './OutlinedInput';
export { default as SvgIcon } from './SvgIcon';
export { default as Grid } from './Grid';
export { default as Autocomplete } from './Autocomplete';
export type { AutocompleteRenderInputParams } from './Autocomplete';
export { default as Container } from './Container';
export { default as Avatar } from './Avatar';
export { default as Card } from './Card';
export { default as Checkbox } from './Checkbox';
export { default as Drawer } from './Drawer';
export { default as ListItemIcon } from './ListItemIcon';
export { default as Radio } from './Radio';
export { default as RadioGroup } from './RadioGroup';
export { default as Tab } from './Tab';
export { default as Tabs } from './Tabs';
export { default as Popover } from './Popover';

// semantic components
export { default as TaskPaper } from './TaskPaper';
export { default as StatusChip } from './StatusChip';
export { default as PriorityChip } from './PriorityChip';
export { default as EmptyState } from './EmptyState';

// additional DS wrappers
export { default as Badge } from './Badge';
export { default as LinearProgress } from './LinearProgress';
export { default as Switch } from './Switch';

// icons barrel
// Some consumers expect a plain object named `Icons` (e.g. `Icons.CloseRoundedIcon`).
// Use import * as and export a const so the shape is stable across transpilation targets.
export const Icons = _Icons;

export { MobileMainLayout } from "./MobileMainLayout";
export { SectionNavBar } from "./SectionNavBar";
export { ContentPaper } from "./ContentPaper";
